import { createContext, FC, ReactNode, useContext, useState, useEffect } from 'react';
import { Appointment, User, UserSettings } from '../types/index';
import useSWR, { mutate } from 'swr';
import { fetcher } from '../lib/api';
import { getBackendUrl } from '../lib/backend';

// Planes de suscripción definidos en el frontend
export const SUBSCRIPTION_PLANS = {
  free: {
    id: 'free',
    name: 'Prueba Gratuita',
    description: 'Acceso a todas las funciones por 14 días',
    price: 0,
    features: [
      'Mensajes de chat ilimitados',
      'Minutos de voz ilimitados',
      'Mensajes de WhatsApp ilimitados',
      'Duración: 14 días',
    ],
  },
  professional: {
    id: 'professional',
    name: 'Profesional',
    description: 'Chat, voz y WhatsApp sin límites',
    price: 19,
    features: [
      'Mensajes de chat ilimitados',
      'Minutos de voz ilimitados',
      'Mensajes de WhatsApp ilimitados',
    ],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    description: 'Todas las funciones profesionales más línea telefónica (próximamente)',
    price: 49,
    features: [
      'Mensajes de chat ilimitados',
      'Minutos de voz ilimitados',
      'Mensajes de WhatsApp ilimitados',
      'Línea telefónica (próximamente)',
    ],
  },
};

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  features: string[];
}

export interface UserSubscription {
  plan: string;
  status: string;
  currentPeriodEnd: string;
  trialExpiry?: string;
}

interface AgentContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  preferences: UserSettings | null;
  preferencesLoading: boolean;
  subscription: UserSubscription | null;
  refreshUserData: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshPreferences: () => Promise<void>;
  getSubscriptionWithDetails: () => {
    plan: string;
    planDetails: SubscriptionPlan;
    status: string;
    currentPeriodEnd: string;
    trialExpiry?: string;
  } | null;
  setPreferences: (preferences: UserSettings | null) => void;
  appointments: Appointment[];
  setAppointments: (appointments: Appointment[]) => void;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export type AgentProviderProps = {
  children: ReactNode;
};

export const AgentProvider: FC<AgentProviderProps> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<UserSettings | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const backendUrl = getBackendUrl();

  const { data: authData, isLoading: authLoading } = useSWR(
    `${backendUrl}/auth/status`,
    fetcher,
    {
      shouldRetryOnError: true,
      errorRetryCount: 2,
      revalidateOnFocus: false,
    },
  );

  const isAuthenticated = authData?.isAuthenticated === true;

  const {
    data: userData,
    error: userError,
    isLoading: userLoading,
  } = useSWR(isAuthenticated ? `${backendUrl}/api/user/me` : null, fetcher, {
    shouldRetryOnError: true,
    errorRetryCount: 2,
    revalidateOnFocus: false,
  });

  const { data: subscriptionData, error: subscriptionError } = useSWR(
    isAuthenticated ? `${backendUrl}/api/subscription` : null,
    fetcher,
    {
      shouldRetryOnError: true,
      errorRetryCount: 2,
      revalidateOnFocus: false,
    },
  );

  const { data: preferencesData, isLoading: preferencesLoading } = useSWR(
    isAuthenticated ? `${backendUrl}/api/preferences` : null,
    fetcher,
    {
      shouldRetryOnError: true,
      errorRetryCount: 2,
      revalidateOnFocus: false,
    },
  );

  const user: User | null = userData || null;
  const subscription: UserSubscription | null = subscriptionData?.subscription || null;
  const loading = authLoading || (isAuthenticated && userLoading);

  useEffect(() => {
    if (preferencesData) {
      setPreferences(preferencesData);
    } else if (!isAuthenticated) {
      setPreferences(null);
    }
  }, [preferencesData, isAuthenticated]);

  const getSubscriptionWithDetails = () => {
    if (!subscription) return null;

    return {
      plan: subscription.plan,
      planDetails: SUBSCRIPTION_PLANS[subscription.plan as keyof typeof SUBSCRIPTION_PLANS],
      status: subscription.status,
      currentPeriodEnd: subscription.currentPeriodEnd,
      trialExpiry: subscription.trialExpiry,
    };
  };

  useEffect(() => {
    if (userError) {
      console.error('Error al cargar datos del usuario:', userError);
      setError('No se pudieron cargar los datos del usuario');
    }
    if (subscriptionError) {
      console.error('Error al cargar suscripción:', subscriptionError);
    }
  }, [userError, subscriptionError]);

  const refreshUserData = async (): Promise<void> => {
    await mutate(`${backendUrl}/api/user/me`);
    await mutate(`${backendUrl}/auth/status`);
  };

  const refreshSubscription = async (): Promise<void> => {
    await mutate(`${backendUrl}/api/subscription`);
  };

  const refreshPreferences = async (): Promise<void> => {
    await mutate(`${backendUrl}/api/preferences`);
  };

  return (
    <AgentContext.Provider
      value={{
        user,
        loading,
        error,
        isAuthenticated,
        authLoading,
        subscription,
        refreshUserData,
        refreshSubscription,
        refreshPreferences,
        getSubscriptionWithDetails,
        preferences,
        preferencesLoading,
        setPreferences,
        appointments,
        setAppointments,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export const useAgentContext = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within a AgentProvider');
  }
  return context;
};

// Export the same hook with a different name for backward compatibility
export const useUser = () => {
  return useAgentContext();
};
